import { Download } from 'lucide-react';
import { useMemo } from 'react';

import type { TablaSpec } from '../asistente.types';

interface AssistantTableProps {
  tabla: TablaSpec;
}

/** `monto_total` → `Monto total` */
const humanizar = (clave: string): string => {
  const texto = clave.replace(/_/g, ' ').trim();

  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

/** Un valor de una celda tal como llega del backend. */
type ValorCelda = string | number | boolean | null | undefined;

/** Un valor listo para mostrar: números con separador de miles, nulos como «—». */
function valorVisible(valor: ValorCelda): string {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'Sí' : 'No';

  if (typeof valor === 'number') {
    return new Intl.NumberFormat('es-PE', { maximumFractionDigits: 2 }).format(valor);
  }

  return String(valor);
}

/** Celda numérica (se alinea a la derecha para poder comparar cifras). */
function esNumero(valor: ValorCelda): boolean {
  return typeof valor === 'number';
}

/**
 * Tabla con los datos exactos que devolvió la consulta.
 *
 * El modelo redacta la respuesta, así que puede equivocarse al transcribir una
 * cifra; esta tabla se arma en el backend con el resultado de la herramienta y
 * se puede descargar para trabajar fuera del panel.
 */
export function AssistantTable({ tabla }: AssistantTableProps) {
  const filas = tabla?.filas ?? [];
  const columnas = tabla?.columnas ?? [];

  const csv = useMemo(() => {
    const escapar = (valor: unknown) => `"${String(valor ?? '').replace(/"/g, '""')}"`;
    const lineas = [
      columnas.map(escapar).join(';'),
      ...filas.map((fila) => columnas.map((columna) => escapar(fila[columna])).join(';')),
    ];

    return lineas.join('\r\n');
  }, [columnas, filas]);

  if (filas.length === 0 || columnas.length === 0) return null;

  /** Descarga la tabla como CSV (punto y coma, que Excel abre directamente). */
  const descargar = () => {
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');

    enlace.href = url;
    enlace.download = `${tabla.titulo.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').toLowerCase()}.csv`;
    enlace.click();
    URL.revokeObjectURL(url);
  };

  return (
    <figure className="mt-3 overflow-hidden rounded-xl border border-ink-100">
      <figcaption className="flex items-center justify-between gap-2 border-b border-ink-100 bg-ink-50/60 px-3 py-1.5">
        <span className="truncate text-[11px] font-semibold text-ink-600">{tabla.titulo}</span>

        <button
          type="button"
          onClick={descargar}
          className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg px-1.5 py-0.5 text-[11px] text-ink-500 transition-colors hover:bg-white hover:text-brand-600"
        >
          <Download className="size-3" aria-hidden="true" />
          CSV
        </button>
      </figcaption>

      <div className="max-h-72 overflow-auto">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-white/95 backdrop-blur">
            <tr>
              {columnas.map((columna) => (
                <th
                  key={columna}
                  scope="col"
                  className="whitespace-nowrap border-b border-ink-100 px-3 py-1.5 text-left font-semibold text-ink-500"
                >
                  {humanizar(columna)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filas.map((fila, indice) => (
              <tr key={indice} className="odd:bg-white even:bg-ink-50/40">
                {columnas.map((columna) => (
                  <td
                    key={columna}
                    className={
                      esNumero(fila[columna])
                        ? 'whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-ink-800'
                        : 'px-3 py-1.5 text-ink-700'
                    }
                  >
                    {valorVisible(fila[columna])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
